/**
 * Global API Service
 * Centralized API request handler with 401 handling, error handling, and toast notifications
 */

import { API_CONFIG, API_ENDPOINTS } from '../config/api.config'
import { showError, showWarning } from '../utils/toast'

/**
 * Handle 401 Unauthorized - redirect to login
 */
function handleUnauthorized() {
  // Clear authentication tokens
  localStorage.removeItem('operator_token')
  
  // Show error message
  showError('Your session has expired. Please login again.')
  
  // Redirect to login page
  setTimeout(() => {
    window.location.href = '/login'
  }, 1500)
}

/**
 * Global API Request Handler
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise} Response data
 */
export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('operator_token')
  
  // Create AbortController for timeout if not provided
  let abortController = null
  let timeoutId = null
  
  if (!options.signal) {
    abortController = new AbortController()
    timeoutId = setTimeout(() => {
      abortController.abort()
    }, API_CONFIG.TIMEOUT)
  }
  
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      signal: options.signal || abortController?.signal,
    })
    
    // Clear timeout if request succeeds
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Handle 401 Unauthorized globally
    if (response.status === 401) {
      handleUnauthorized()
      throw new Error('Unauthorized')
    }

    // Handle CORS errors
    if (!response.ok && response.status === 0) {
      throw new Error(`CORS Error: Cannot connect to ${API_CONFIG.BASE_URL}. Make sure the API server is running and CORS is configured.`)
    }

    const data = await response.json()

    if (!response.ok) {
      // Extract specific validation errors if available
      let errorMessage = data.error?.message || data.message || 'Request failed'
      
      // If there are field-specific validation errors, use the first one
      if (data.error?.errors && typeof data.error.errors === 'object') {
        const fieldErrors = Object.values(data.error.errors).flat()
        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors[0] // Use the first specific error message
        }
      }
      
      // Create error with additional context
      const error = new Error(errorMessage)
      error.response = data
      error.status = response.status
      error.errors = data.error?.errors || {}
      throw error
    }

    return data.data || data
  } catch (error) {
    // Clear timeout on error
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    
    // Handle network errors
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      throw new Error('Request timeout. Please check your connection and try again.')
    }
    
    if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
      throw new Error(`Cannot connect to API at ${API_CONFIG.BASE_URL}. Check if the server is running and CORS is configured.`)
    }
    
    // Re-throw other errors
    throw error
  }
}

/**
 * GET request
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @returns {Promise} Response data
 */
export async function apiGet(endpoint, params = {}) {
  const queryParams = new URLSearchParams()
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      queryParams.append(key, params[key])
    }
  })
  
  const query = queryParams.toString()
  const url = `${endpoint}${query ? `?${query}` : ''}`
  
  return apiRequest(url, {
    method: 'GET',
  })
}

/**
 * POST request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export async function apiPost(endpoint, data = {}) {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * PUT request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export async function apiPut(endpoint, data = {}) {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * PATCH request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @returns {Promise} Response data
 */
export async function apiPatch(endpoint, data = {}) {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * DELETE request
 * @param {string} endpoint - API endpoint
 * @returns {Promise} Response data
 */
export async function apiDelete(endpoint) {
  return apiRequest(endpoint, {
    method: 'DELETE',
  })
}

// Export API endpoints for convenience
export { API_ENDPOINTS }
